import React from "react";
import { useTranslate } from "@refinedev/core";
import { DateField } from "@refinedev/antd";
import {
    Modal,
    Tabs,
    Card,
    Space,
    Flex,
    Typography,
    Tag,
    Button,
    Tooltip,
    Table,
    message,
    Progress,
    Descriptions,
    Divider,
} from "antd";
import { CopyOutlined, InfoCircleOutlined, DownloadOutlined, SendOutlined } from "@ant-design/icons";

interface AnalysisResultsModalProps {
    open: boolean;
    onCancel: () => void;
    analysisResult: any;
    tableColumns: any[];
    getTableData: () => any[];
    attachments: any[];
    isAssembling: boolean;
    onRowClick: (record: any) => void;
    onFileDownload: (fileData?: any) => void;
    onAssemble: (record: any) => void;
}

export const AnalysisResultsModal: React.FC<AnalysisResultsModalProps> = ({
    open,
    onCancel,
    analysisResult,
    tableColumns,
    getTableData,
    attachments,
    isAssembling,
    onRowClick,
    onFileDownload,
    onAssemble,
}) => {
    const _t = useTranslate();

    return (
        <Modal
            title={_t("orders.automationTasks.matchedFiles.title")}
            open={open}
            onCancel={onCancel}
            width={950}
            footer={null}
        >
            <Tabs
                defaultActiveKey={analysisResult?.bestMatchingFile ? "best-match" : "full-list"}
                items={[
                    {
                        key: "best-match",
                        label: _t("orders.automationTasks.matchedFiles.bestMatchTab"),
                        children: analysisResult?.bestMatchingFile ? (() => {
                            const bestMatch = analysisResult.bestMatchingFile;
                            const bestFile = bestMatch.file;
                            const tags: string[] = bestFile?.tags ?? [];
                            const rawScore = bestMatch.score;
                            const numericScore =
                                typeof rawScore === "number"
                                    ? Math.round(rawScore * 100)
                                    : null;
                            const scoreColor =
                                numericScore == null
                                    ? "#1677ff"
                                    : numericScore >= 80
                                      ? "#52c41a"
                                      : numericScore >= 50
                                        ? "#faad14"
                                        : "#f5222d";

                            const handleCopyProjectRef = () => {
                                navigator.clipboard
                                    .writeText(bestMatch.projectRef)
                                    .then(
                                        () =>
                                            message.success(
                                                _t(
                                                    "orders.automationTasks.fileDetailsModal.copied"
                                                )
                                            ),
                                        () =>
                                            message.error(
                                                _t(
                                                    "orders.automationTasks.fileDetailsModal.copyFailed"
                                                )
                                            )
                                    );
                            };

                            return (
                                <Card
                                    title={_t(
                                        "orders.automationTasks.matchedFiles.bestMatchCardTitle"
                                    )}
                                    size="small"
                                    className="mb-0"
                                >
                                    <Flex
                                        align="center"
                                        gap="large"
                                        wrap="wrap"
                                        className="w-full"
                                    >
                                        <Flex
                                            vertical
                                            align="center"
                                            justify="center"
                                            className="w-[180px] shrink-0 text-center"
                                        >
                                            {numericScore != null ? (
                                                <Progress
                                                    type="circle"
                                                    percent={numericScore}
                                                    size={120}
                                                    strokeColor={scoreColor}
                                                    format={(percent) => (
                                                        <Flex
                                                            vertical
                                                            align="center"
                                                            justify="center"
                                                        >
                                                            <Typography.Text
                                                                strong
                                                                style={{
                                                                    fontSize: 28,
                                                                    lineHeight: 1.1,
                                                                    color: scoreColor,
                                                                }}
                                                            >
                                                                {percent}%
                                                            </Typography.Text>
                                                            <Typography.Text
                                                                type="secondary"
                                                                style={{
                                                                    fontSize: 11,
                                                                    lineHeight: 1.2,
                                                                }}
                                                            >
                                                                {_t(
                                                                    "orders.automationTasks.matchedFiles.matchScore"
                                                                )}
                                                            </Typography.Text>
                                                        </Flex>
                                                    )}
                                                />
                                            ) : (
                                                <>
                                                    <Typography.Title
                                                        level={2}
                                                        className="!mb-0"
                                                        style={{ color: scoreColor }}
                                                    >
                                                        {String(rawScore)}
                                                    </Typography.Title>
                                                    <Typography.Text
                                                        type="secondary"
                                                        className="mt-2"
                                                    >
                                                        {_t(
                                                            "orders.automationTasks.matchedFiles.matchScore"
                                                        )}
                                                    </Typography.Text>
                                                </>
                                            )}
                                        </Flex>
                                        <div className="min-w-0 flex-1">
                                            <Descriptions
                                                column={1}
                                                size="small"
                                                colon={false}
                                            >
                                                <Descriptions.Item
                                                    label={_t(
                                                        "orders.automationTasks.matchedFiles.projectRef"
                                                    )}
                                                >
                                                    <Space size="small" wrap>
                                                        <Typography.Text code>
                                                            {bestMatch.projectRef}
                                                        </Typography.Text>
                                                        <Tooltip
                                                            title={_t(
                                                                "orders.automationTasks.fileDetailsModal.copyProjectRef"
                                                            )}
                                                        >
                                                            <Button
                                                                type="text"
                                                                icon={<CopyOutlined />}
                                                                size="small"
                                                                onClick={handleCopyProjectRef}
                                                            />
                                                        </Tooltip>
                                                    </Space>
                                                </Descriptions.Item>
                                                {bestFile?.type ? (
                                                    <Descriptions.Item
                                                        label={_t(
                                                            "orders.automationTasks.matchedFiles.originalVersionName"
                                                        )}
                                                    >
                                                        {bestFile.type}
                                                    </Descriptions.Item>
                                                ) : null}
                                                {tags.length > 0 ? (
                                                    <Descriptions.Item
                                                        label={_t(
                                                            "orders.automationTasks.matchedFiles.tags"
                                                        )}
                                                    >
                                                        <Space size={[0, 8]} wrap>
                                                            {tags.map(
                                                                (tag: string, index: number) => (
                                                                    <Tag key={index}>{tag}</Tag>
                                                                )
                                                            )}
                                                        </Space>
                                                    </Descriptions.Item>
                                                ) : null}
                                                {bestFile?.createdAt ? (
                                                    <Descriptions.Item
                                                        label={_t(
                                                            "orders.automationTasks.fileDetailsModal.createdAt"
                                                        )}
                                                    >
                                                        <DateField
                                                            value={bestFile.createdAt}
                                                            format="DD/MM/YYYY HH:mm"
                                                        />
                                                    </Descriptions.Item>
                                                ) : null}
                                            </Descriptions>
                                        </div>
                                    </Flex>
                                    <Divider className="my-3" />
                                    <Flex
                                        justify="space-between"
                                        align="center"
                                        wrap="wrap"
                                        gap="small"
                                    >
                                        <Space wrap>
                                            <Button
                                                icon={<InfoCircleOutlined />}
                                                onClick={() =>
                                                    onRowClick({
                                                        fileData: bestFile,
                                                        projectRef: bestMatch.projectRef,
                                                    })
                                                }
                                            >
                                                {_t(
                                                    "orders.automationTasks.matchedFiles.info"
                                                )}
                                            </Button>
                                            {bestFile?.downloadUrl && (
                                                <Button
                                                    icon={<DownloadOutlined />}
                                                    onClick={() => onFileDownload(bestFile)}
                                                >
                                                    {_t(
                                                        "orders.automationTasks.matchedFiles.download"
                                                    )}
                                                </Button>
                                            )}
                                        </Space>
                                        {attachments && attachments.length > 0 && (
                                            <Button
                                                type="primary"
                                                icon={<SendOutlined />}
                                                onClick={() =>
                                                    onAssemble({
                                                        fileData: bestFile,
                                                        projectRef: bestMatch.projectRef,
                                                    })
                                                }
                                                loading={isAssembling}
                                                disabled={isAssembling}
                                            >
                                                {_t(
                                                    "orders.automationTasks.matchedFiles.assemble"
                                                )}
                                            </Button>
                                        )}
                                    </Flex>
                                </Card>
                            );
                        })() : (
                            <Typography.Text type="secondary">
                                {_t("orders.automationTasks.matchedFiles.noBestMatch")}
                            </Typography.Text>
                        ),
                    },
                    {
                        key: "full-list",
                        label: _t("orders.automationTasks.matchedFiles.fullListTab"),
                        children: (
                            <Table
                                columns={tableColumns}
                                dataSource={getTableData()}
                                pagination={{
                                    pageSize: 10,
                                    showSizeChanger: true,
                                    showTotal: (total) =>
                                        _t("orders.automationTasks.matchedFiles.totalFiles", { total }),
                                }}
                                scroll={{ x: "max-content" }}
                                defaultExpandAllRows={true}
                            />
                        ),
                    },
                ]}
            />
        </Modal>
    );
};
